<template>
  <transition name="modal-fade">
    <div v-if="modelValue" class="modal-overlay" @click.self="closeModal">
      <div ref="modalContainer" class="modal-container">
        <div class="modal-header">
          <h2 class="modal-title">
            <IconSvg name="plane-solid-full" size="md" /> 新規しおり作成
          </h2>
          <button
            type="button"
            class="modal-close-btn"
            @click="closeModal"
            :disabled="isSubmitting"
          >
            ×
          </button>
        </div>

        <!-- エラーメッセージ表示 -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <form @submit.prevent="handleSubmit" class="modal-form">
          <div class="form-group">
            <label class="form-label">
              タイトル <span class="required">*</span>
            </label>
            <input
              v-model="formData.title"
              type="text"
              class="form-input"
              :class="{ 'input-error': errors.title }"
              placeholder="例: 長野_上高地"
              maxlength="255"
              required
            />
            <p v-if="errors.title" class="error-text">
              {{ errors.title }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">
              開始日 <span class="required">*</span>
            </label>
            <input
              v-model="formData.startDate"
              type="date"
              class="form-input"
              :class="{ 'input-error': errors.startDate }"
              min="1900-01-01"
              max="9999-12-31"
              @input="handleStartDateChange"
              @change="handleStartDateChange"
              @blur="validateYear('startDate')"
              required
            />
            <p v-if="errors.startDate" class="error-text">
              {{ errors.startDate }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">
              終了日 <span class="required">*</span>
            </label>
            <input
              v-model="formData.endDate"
              type="date"
              class="form-input"
              :class="{ 'input-error': errors.endDate }"
              min="1900-01-01"
              max="9999-12-31"
              @input="validateYear('endDate')"
              @change="validateYear('endDate')"
              @blur="validateYear('endDate')"
              required
            />
            <p v-if="errors.endDate" class="error-text">
              {{ errors.endDate }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">予算（任意）</label>
            <div class="budget-input-wrapper">
              <span class="currency-symbol">¥</span>
              <input
                v-model.number="formData.budget"
                type="number"
                class="form-input budget-input"
                :class="{ 'input-error': errors.budget }"
                min="0"
                step="1"
                placeholder="例: 50000"
              />
            </div>
            <p v-if="errors.budget" class="error-text">
              {{ errors.budget }}
            </p>
          </div>

          <!-- 渡航先選択 -->
          <div class="form-group">
            <label class="form-label">
              旅行先 <span v-if="!isDayTrip" class="required">*</span>
            </label>
            <div class="destination-type-selector">
              <button
                type="button"
                class="destination-type-btn"
                :class="{ active: formData.destinationType === 'domestic' }"
                @click="formData.destinationType = 'domestic'"
              >
                JP 国内
              </button>
              <button
                type="button"
                class="destination-type-btn"
                :class="{
                  active: formData.destinationType === 'international',
                }"
                @click="formData.destinationType = 'international'"
              >
                <IconSvg name="earth-americas-solid-full" size="sm" /> 海外
              </button>
            </div>

            <!-- 渡航先入力エリア -->
            <div class="destinations-container">
              <div
                v-for="(destination, index) in formData.destinations"
                :key="index"
                class="destination-input-row"
              >
                <div class="destination-autocomplete">
                  <input
                    v-model="destination.value"
                    @input="handleDestinationInput(index)"
                    @focus="showSuggestions(index)"
                    @blur="hideSuggestions(index)"
                    type="text"
                    class="form-input"
                    :placeholder="
                      formData.destinationType === 'domestic'
                        ? '都道府県名を入力'
                        : '国名を入力'
                    "
                  />
                  <!-- 候補リスト -->
                  <div
                    v-if="
                      destination.showSuggestions &&
                      destination.filteredList.length > 0
                    "
                    class="suggestions-list"
                  >
                    <div
                      v-for="(suggestion, sIndex) in destination.filteredList"
                      :key="sIndex"
                      class="suggestion-item"
                      @mousedown.prevent="selectDestination(index, suggestion)"
                    >
                      {{ suggestion }}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  @click="removeDestination(index)"
                  class="remove-member-btn"
                  :class="{ 'always-visible': true }"
                  :disabled="formData.destinations.length === 1"
                  title="削除"
                >
                  ×
                </button>
              </div>
              <button
                type="button"
                @click="addDestination"
                class="add-member-btn"
                :disabled="formData.destinations.length >= 10"
              >
                ＋ 旅行先を追加
              </button>
            </div>
            <p v-if="errors.destinations" class="error-text">
              {{ errors.destinations }}
            </p>
            <p v-else-if="isDayTrip" class="form-help">
              日帰りの場合、旅行先は未入力でも作成できます。
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">
              メンバー <span class="required">*</span>
            </label>
            <div class="member-add-tools">
              <button
                type="button"
                class="friend-list-toggle"
                @click="toggleFriendList"
              >
                <IconSvg name="user-solid-full" size="sm" />
                友達リストから追加
              </button>
            </div>
            <div v-if="isFriendListOpen" class="friend-picker">
              <input
                v-model.trim="friendSearch"
                type="search"
                class="form-input friend-search-input"
                placeholder="名前またはメールで検索"
                @input="fetchFriendCandidates"
              />
              <div v-if="friendCandidates.length === 0" class="friend-empty">
                追加できるユーザーが見つかりません
              </div>
              <button
                v-for="friend in friendCandidates"
                :key="friend.APP_USER_CODE"
                type="button"
                class="friend-option"
                :disabled="isFriendSelected(friend)"
                @click="addFriendMember(friend)"
              >
                <span class="friend-avatar">
                  <img
                    v-if="friend.PROFILE_IMAGE_BASE64"
                    :src="friend.PROFILE_IMAGE_BASE64"
                    :alt="friend.USER_NAME"
                    loading="lazy"
                    decoding="async"
                  />
                  <IconSvg v-else name="user-solid-full" size="sm" />
                </span>
                <span>
                  <strong>{{ friend.USER_NAME }}</strong>
                  <small>{{ friend.EMAIL }}</small>
                </span>
                <span class="friend-add-state">
                  {{ isFriendSelected(friend) ? "追加済み" : "追加" }}
                </span>
              </button>
            </div>
            <div class="members-input-container">
              <div
                v-for="(member, index) in formData.members"
                :key="index"
                class="member-input-card"
              >
                <div class="member-input-row">
                  <input
                    v-model="member.name"
                    type="text"
                    class="form-input member-input"
                    :class="{
                      'input-error': errors.members && errors.members[index],
                    }"
                    :placeholder="`メンバー${index + 1}の名前`"
                    maxlength="255"
                    :readonly="Boolean(member.appUserCode)"
                  />
                  <button
                    v-if="index !== 0"
                    type="button"
                    @click="toggleMemberEmail(index)"
                    class="member-email-toggle-btn"
                    :class="{ active: member.showEmail || member.email }"
                    title="メールで招待"
                  >
                    <IconSvg name="envelope-solid-full" size="sm" />
                  </button>
                  <button
                    v-if="index !== 0"
                    type="button"
                    @click="removeMember(index)"
                    class="remove-member-btn"
                    :disabled="formData.members.length === 1"
                    title="削除"
                  >
                    ×
                  </button>
                </div>
                <input
                  v-if="index !== 0 && member.showEmail"
                  v-model.trim="member.email"
                  type="email"
                  class="form-input member-email-input"
                  placeholder="招待メール"
                  autocomplete="email"
                />
              </div>
              <button
                type="button"
                @click="addMember"
                class="add-member-btn"
                :disabled="formData.members.length >= 20"
              >
                ＋ メンバーを追加
              </button>
            </div>
            <p
              v-if="errors.members && typeof errors.members === 'string'"
              class="error-text"
            >
              {{ errors.members }}
            </p>
          </div>

          <div class="form-group">
            <label class="form-label"> 旅行の写真（任意） </label>
            <div class="image-upload-area">
              <input
                ref="imageInput"
                type="file"
                accept="image/*,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.hif"
                @change="handleImageSelect"
                class="image-input"
                id="image-upload"
              />
              <label
                v-if="!displayImageUrl"
                for="image-upload"
                class="image-upload-label"
                @click.prevent="openImagePicker"
              >
                <span class="upload-icon">
                  <IconSvg name="camera-solid-full" size="lg" />
                </span>
                <span class="upload-text">画像を選択</span>
                <span class="upload-hint"
                  >クリックして画像を選択（最大25MB）</span
                >
              </label>
              <div v-else class="image-preview-container">
                <img
                  :src="displayImageUrl"
                  :alt="displayImageAlt"
                  width="1600"
                  height="900"
                  class="image-preview"
                  decoding="async"
                  @error="handleDisplayImageError"
                />
                <button
                  type="button"
                  @click="openImagePicker"
                  class="change-image-btn"
                >
                  画像を変更
                </button>
                <button
                  v-if="isManualImagePreview"
                  type="button"
                  @click="removeImage"
                  class="remove-image-btn"
                  aria-label="選択した画像を削除"
                  title="画像を削除"
                >
                  ×
                </button>
              </div>
            </div>
            <p v-if="errors.image" class="error-text">
              {{ errors.image }}
            </p>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              @click="closeModal"
              class="btn btn-secondary"
              :disabled="isSubmitting"
            >
              キャンセル
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? "作成中..." : "作成" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import { computed, nextTick, ref, watch, onMounted } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import { useShioriStore } from "@/store/shiori-store.js";
import { useShioriMemberStore } from "@/store/shiori-member-store.js";
import { useAuthStore } from "@/store/authStore.js";
import { useDestinationMasterStore } from "@/store/destination-master-store.js";
import { filterDestinationSuggestions } from "@/data/destinationSuggestions.js";
import axios from "axios";
import { API_URL } from "@/config/api.js";
import { isGuestMode } from "@/utils/guest-session.js";
import { fetchTemplateImageFile } from "@/utils/shiori-template.js";

export default {
  name: "CreateShioriModal",
  components: {
    IconSvg,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    prefillData: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue", "create", "template-create-failed"],
  setup(props, { emit }) {
    const shioriStore = useShioriStore();
    const shioriMemberStore = useShioriMemberStore();
    const authStore = useAuthStore();
    const destMasterStore = useDestinationMasterStore();

    onMounted(() => destMasterStore.fetchAll());

    // ログインユーザー名を初期メンバーとして設定
    const initialMember = authStore.user?.USER_NAME || "";

    const formData = ref({
      title: "",
      startDate: "",
      endDate: "",
      budget: null,
      destinationType: "domestic", // domestic or international
      destinations: [
        {
          value: "",
          showSuggestions: false,
          filteredList: [],
        },
      ],
      members: [
        { name: initialMember, email: "", appUserCode: null, showEmail: false },
      ],
    });

    const errors = ref({
      title: "",
      startDate: "",
      endDate: "",
      budget: "",
      destinations: "",
      members: "",
      image: "",
    });

    const errorMessage = ref("");
    const isSubmitting = ref(false);
    const imageInput = ref(null);
    const selectedImage = ref(null);
    const imagePreview = ref(null);
    const templateImageLoadFailed = ref(false);
    const modalContainer = ref(null);
    const isFriendListOpen = ref(false);
    const friendSearch = ref("");
    const friendCandidates = ref([]);
    const isDayTrip = computed(
      () =>
        Boolean(formData.value.startDate) &&
        formData.value.startDate === formData.value.endDate,
    );
    const isManualImagePreview = computed(() => Boolean(imagePreview.value));
    const displayImageUrl = computed(() => {
      if (imagePreview.value) return imagePreview.value;
      if (templateImageLoadFailed.value) return null;
      return props.prefillData?.templateImageUrl || null;
    });
    const displayImageAlt = computed(() =>
      isManualImagePreview.value
        ? "選択した旅行写真のプレビュー"
        : props.prefillData?.templateImageAlt || "旅行テンプレートのイメージ",
    );

    const openImagePicker = () => {
      imageInput.value?.click();
    };

    const handleImageSelect = (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // ファイルサイズチェック（25MB）
      if (file.size > 25 * 1024 * 1024) {
        errors.value.image = "画像サイズは25MB以下にしてください";
        event.target.value = "";
        return;
      }

      const extension = file.name.toLowerCase().split(".").pop();
      const supportedExtensions = [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "heic",
        "heif",
        "hif",
      ];
      const supportedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/heic",
        "image/heif",
      ];
      const hasSupportedExtension = supportedExtensions.includes(extension);
      const hasSupportedType = supportedTypes.includes(
        file.type.toLowerCase(),
      );
      if (!hasSupportedExtension && !hasSupportedType) {
        errors.value.image =
          "JPEG、PNG、GIF、WebP、HEIC/HEIF形式を選択してください";
        event.target.value = "";
        return;
      }

      selectedImage.value = file;
      errors.value.image = "";

      // プレビュー表示
      const reader = new FileReader();
      reader.onload = (e) => {
        imagePreview.value = e.target.result;
      };
      reader.readAsDataURL(file);
    };

    const removeImage = () => {
      selectedImage.value = null;
      imagePreview.value = null;
      if (imageInput.value) {
        imageInput.value.value = "";
      }
    };

    const handleDisplayImageError = () => {
      if (isManualImagePreview.value) {
        removeImage();
        return;
      }
      templateImageLoadFailed.value = true;
    };

    const fetchFriendCandidates = async () => {
      try {
        friendCandidates.value =
          await shioriMemberStore.fetchFriendCandidates(friendSearch.value);
      } catch (error) {
        console.error("友達リスト取得エラー:", error);
        friendCandidates.value = [];
      }
    };

    const toggleFriendList = async () => {
      isFriendListOpen.value = !isFriendListOpen.value;
      if (isFriendListOpen.value && friendCandidates.value.length === 0) {
        await fetchFriendCandidates();
      }
    };

    const isFriendSelected = (friend) =>
      formData.value.members.some(
        (member) => member.appUserCode === friend.APP_USER_CODE,
      );

    const addFriendMember = (friend) => {
      if (isFriendSelected(friend) || formData.value.members.length >= 20) {
        return;
      }
      formData.value.members.push({
        name: friend.USER_NAME || friend.EMAIL,
        email: "",
        appUserCode: friend.APP_USER_CODE,
        showEmail: false,
      });
    };

    const addMember = () => {
      if (formData.value.members.length < 20) {
        formData.value.members.push({
          name: "",
          email: "",
          appUserCode: null,
          showEmail: false,
        });
      }
    };

    const toggleMemberEmail = (index) => {
      const member = formData.value.members[index];
      member.showEmail = !member.showEmail;
    };

    const removeMember = (index) => {
      // 最初のメンバー（自分自身）は削除できない
      if (index === 0) {
        return;
      }
      if (formData.value.members.length > 1) {
        formData.value.members.splice(index, 1);
      }
    };

    // 渡航先関連の関数
    const addDestination = () => {
      if (formData.value.destinations.length < 10) {
        formData.value.destinations.push({
          value: "",
          showSuggestions: false,
          filteredList: [],
        });
      }
    };

    const removeDestination = (index) => {
      if (formData.value.destinations.length > 1) {
        formData.value.destinations.splice(index, 1);
      }
    };

    const handleDestinationInput = (index) => {
      const destination = formData.value.destinations[index];
      const inputValue = destination.value;
      destination.filteredList = filterDestinationSuggestions({
        inputValue,
        destinationType: formData.value.destinationType,
        prefectureNames: destMasterStore.prefectureNames,
        countryRows: destMasterStore.countries,
        countryAliasRows: destMasterStore.countryAliases,
      });
    };

    const showSuggestions = (index) => {
      formData.value.destinations[index].showSuggestions = true;
      handleDestinationInput(index);
    };

    const hideSuggestions = (index) => {
      setTimeout(() => {
        formData.value.destinations[index].showSuggestions = false;
      }, 200);
    };

    const selectDestination = (index, suggestion) => {
      formData.value.destinations[index].value = suggestion;
      formData.value.destinations[index].showSuggestions = false;
      formData.value.destinations[index].filteredList = [];
    };

    const closeModal = () => {
      emit("update:modelValue", false);
      clearErrors();
    };

    const clearErrors = () => {
      errors.value = {
        title: "",
        startDate: "",
        endDate: "",
        budget: "",
        destinations: "",
        members: "",
        image: "",
      };
      errorMessage.value = "";
    };

    const validateYear = (fieldName) => {
      const dateValue = formData.value[fieldName];
      if (!dateValue) return;

      // 年の部分を抽出して検証（YYYY-MM-DD形式）
      const yearMatch = dateValue.match(/^(\d+)-/);
      if (yearMatch) {
        const year = yearMatch[1];

        // 年が3桁以下の場合は編集中と判断して何もしない
        if (year.length < 4) {
          return;
        }

        // 年が4桁を超える場合のみ、最初の4桁に切り詰め
        if (year.length > 4) {
          const validYear = year.substring(0, 4);
          const [, month, day] = dateValue.split("-");
          formData.value[fieldName] = `${validYear}-${month || "01"}-${
            day || "01"
          }`;
          return;
        }

        // 年が正確に4桁の場合のみ範囲チェック
        if (year.length === 4) {
          const yearNum = parseInt(year);
          if (yearNum < 1900) {
            formData.value[fieldName] = `1900-${
              dateValue.split("-")[1] || "01"
            }-${dateValue.split("-")[2] || "01"}`;
          } else if (yearNum > 9999) {
            formData.value[fieldName] = `9999-${
              dateValue.split("-")[1] || "12"
            }-${dateValue.split("-")[2] || "31"}`;
          }
        }
      }
    };

    const toLocalDateKey = (date) =>
      [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");

    const addDays = (dateKey, days) => {
      const [year, month, day] = dateKey.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() + days);
      return toLocalDateKey(date);
    };

    const handleStartDateChange = () => {
      validateYear("startDate");
      if (formData.value.startDate) {
        const templateDays = Number(props.prefillData?.days);
        formData.value.endDate =
          Number.isFinite(templateDays) && templateDays > 1
            ? addDays(formData.value.startDate, templateDays - 1)
            : formData.value.startDate;
        validateYear("endDate");
      }
    };

    const validateForm = () => {
      clearErrors();
      let isValid = true;

      // タイトルのバリデーション
      if (!formData.value.title || !formData.value.title.trim()) {
        errors.value.title = "タイトルは必須です";
        isValid = false;
      } else if (formData.value.title.length > 255) {
        errors.value.title = "タイトルは255文字以内で入力してください";
        isValid = false;
      }

      // 開始日のバリデーション
      if (!formData.value.startDate) {
        errors.value.startDate = "開始日は必須です";
        isValid = false;
      }

      // 終了日のバリデーション
      if (!formData.value.endDate) {
        errors.value.endDate = "終了日は必須です";
        isValid = false;
      } else if (formData.value.startDate && formData.value.endDate) {
        const start = new Date(formData.value.startDate);
        const end = new Date(formData.value.endDate);
        if (end < start) {
          errors.value.endDate = "終了日は開始日以降の日付を指定してください";
          isValid = false;
        }
      }

      // 渡航先のバリデーション（日帰りの場合は任意）
      if (!isDayTrip.value) {
        if (
          !formData.value.destinations ||
          formData.value.destinations.length === 0
        ) {
          errors.value.destinations = "旅行先を最低1つ追加してください";
          isValid = false;
        } else {
          // 空の渡航先をチェック
          const emptyDestinations = formData.value.destinations.filter(
            (d) => !d.value || !d.value.trim(),
          );
          if (emptyDestinations.length > 0) {
            errors.value.destinations = "旅行先を入力してください";
            isValid = false;
          }
        }
      }

      if (formData.value.budget != null && formData.value.budget < 0) {
        errors.value.budget = "予算は0円以上で入力してください";
        isValid = false;
      }

      // メンバーのバリデーション
      if (!formData.value.members || formData.value.members.length === 0) {
        errors.value.members = "メンバーを最低1人追加してください";
        isValid = false;
      } else {
        // 空のメンバー名をチェック
        const emptyMembers = formData.value.members.filter(
          (member) =>
            (!member.name || !member.name.trim()) &&
            (!member.email || !member.email.trim()),
        );
        if (emptyMembers.length > 0) {
          errors.value.members =
            "メンバー名、友達リスト、招待メールのいずれかで追加してください";
          isValid = false;
        }

        const invalidEmail = formData.value.members.find(
          (member) =>
            member.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email),
        );
        if (invalidEmail) {
          errors.value.members = "招待メールの形式を確認してください";
          isValid = false;
        }
      }

      return isValid;
    };

    const moveToFirstError = async () => {
      await nextTick();
      const container = modalContainer.value;
      if (!container) return;

      const errorElement =
        container.querySelector(".input-error") ||
        container.querySelector(".error-text");
      if (!errorElement) return;

      errorElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      const focusTarget = errorElement.matches("input, select, textarea")
        ? errorElement
        : errorElement
            .closest(".form-group")
            ?.querySelector("input, select, textarea");
      focusTarget?.focus({ preventScroll: true });
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        errorMessage.value = "入力内容に誤りがあります。修正してください。";
        await moveToFirstError();
        return;
      }

      isSubmitting.value = true;
      errorMessage.value = "";

      try {
        let templateImageFile = null;
        const templateWarnings = [];
        if (props.prefillData?.requiresTemplateImageUpload && !selectedImage.value) {
          try {
            templateImageFile = await fetchTemplateImageFile({
              id: props.prefillData.templateId,
              imageUrl: props.prefillData.templateImageUrl,
            });
          } catch (templateImageError) {
            console.error("テンプレート画像取得エラー:", templateImageError);
            templateWarnings.push({
              stage: "template-image-fetch",
              message: "テンプレート画像を取得できなかったため、画像なしで作成を続けます。",
            });
          }
        }

        // タイトル重複チェック
        if (!isGuestMode()) {
          const token = localStorage.getItem("token");
          const checkResponse = await axios.get(
            `${API_URL}/shiori/check-title/${encodeURIComponent(
              formData.value.title,
            )}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (checkResponse.data.exists) {
            errorMessage.value =
              "同じ名前のしおりが既に存在します。別の名前を入力してください。";
            isSubmitting.value = false;
            return;
          }
        }

        // 渡航先データを整形（valueのみ抽出）
        const destinationsList = formData.value.destinations
          .map((d) => d.value.trim())
          .filter((v) => v);

        // しおり作成APIを呼び出し
        console.log("送信するデータ:", formData.value);
        const shioriData = {
          title: formData.value.title,
          startDate: formData.value.startDate,
          endDate: formData.value.endDate,
          budget: formData.value.budget,
          destinationType: formData.value.destinationType,
          destinations: destinationsList,
        };
        const result = await shioriStore.createShiori(shioriData);

        console.log("しおり作成成功:", result);

        // メンバーを登録
        const shioriNov =
          result.SHIORI_NOV || result.data?.SHIORI_NOV || result.shiori_id;
        if (shioriNov) {
          try {
            const currentUserName =
              authStore.user?.user_name || authStore.user?.USER_NAME;
            const currentUserCode =
              authStore.user?.APP_USER_CODE || authStore.user?.user_id;

            // フォームに入力されたメンバーを登録
            if (formData.value.members.length > 0) {
              for (const member of formData.value.members) {
                const inviteEmail = member.email?.trim() || null;
                const memberName =
                  member.name?.trim() ||
                  (inviteEmail ? inviteEmail.split("@")[0] : "");
                if (memberName || inviteEmail || member.appUserCode) {
                  // ログインユーザーの場合はAPP_USER_CODEを設定
                  const isCurrentUser =
                    memberName === currentUserName && !inviteEmail;
                  const createdMember =
                    await shioriMemberStore.createMember(shioriNov, {
                      member_name: memberName || "招待メンバー",
                      app_user_code: inviteEmail
                        ? null
                        : member.appUserCode ||
                          (isCurrentUser ? currentUserCode : null),
                      invite_email: inviteEmail,
                    });
                  if (inviteEmail && createdMember.invitation_sent === false) {
                    throw new Error(
                      `${inviteEmail} への招待メール送信に失敗しました。`,
                    );
                  }
                }
              }
            }
            console.log("メンバー登録成功");

            // メンバー登録後、ストアのメンバー情報を再取得
            await shioriMemberStore.fetchMembers(shioriNov);
          } catch (memberError) {
            console.error("メンバー登録エラー:", memberError);
            if (props.prefillData?.isTemplate) {
              emit("template-create-failed", {
                shioriId: shioriNov,
                error: memberError,
              });
            }
            errorMessage.value =
              "しおりは作成されましたが、メンバー招待に失敗しました。しおり管理から再度追加してください。";
            isSubmitting.value = false;
            return;
          }
        }

        // 画像が選択されている場合はアップロード
        if (selectedImage.value && shioriNov) {
          try {
            await shioriStore.uploadShioriImage(shioriNov, selectedImage.value);
            console.log("画像アップロード成功");
          } catch (imageError) {
            console.error("画像アップロードエラー:", imageError);
            templateWarnings.push({
              stage: "manual-image-upload",
              message: "画像を設定できませんでした。旅程は作成済みです。しおり管理から再度設定できます。",
            });
          }
        }

        const createdContext = {
          shioriId: shioriNov,
          title: formData.value.title,
          startDate: formData.value.startDate,
          endDate: formData.value.endDate,
          members: formData.value.members.map((member) => ({ ...member })),
          manualImageUploaded: Boolean(selectedImage.value),
          templateImageFile,
          templateWarnings,
        };

        // フォームをリセットしてモーダルを閉じる
        resetForm();
        closeModal();

        // 親コンポーネントに成功を通知（ページ遷移のため）
        emit("create", result, createdContext);
      } catch (error) {
        console.error("しおり作成エラー:", error);
        console.error("エラー詳細:", error.response);

        const status = error.response?.status;
        errorMessage.value = status === 401
          ? "ログインの有効期限が切れました。もう一度ログインしてください。"
          : status === 403
            ? "しおりを作成する権限がありません。"
            : "しおりの作成に失敗しました。もう一度お試しください。";
      } finally {
        isSubmitting.value = false;
      }
    };

    const resetForm = () => {
      formData.value = {
        title: "",
        startDate: "",
        endDate: "",
        budget: null,
        destinationType: "domestic",
        destinations: [
          {
            value: "",
            showSuggestions: false,
            filteredList: [],
          },
        ],
        members: [{ name: "", email: "", appUserCode: null, showEmail: false }],
      };
      removeImage();
      templateImageLoadFailed.value = false;
      isFriendListOpen.value = false;
      friendSearch.value = "";
      friendCandidates.value = [];
      clearErrors();
    };

    const applyPrefillData = () => {
      const prefill = props.prefillData;
      if (!prefill) return;

      formData.value.title = prefill.title || "";
      formData.value.startDate = prefill.startDate || "";
      formData.value.endDate = prefill.endDate || prefill.startDate || "";
      formData.value.budget =
        prefill.budget !== undefined && prefill.budget !== null
          ? Number(prefill.budget)
          : null;
      formData.value.destinationType =
        prefill.destinationType || formData.value.destinationType;
      formData.value.destinations =
        Array.isArray(prefill.destinations) && prefill.destinations.length
          ? prefill.destinations.map((destination) => ({
              value: destination,
              showSuggestions: false,
              filteredList: [],
            }))
          : [
              {
                value: "",
                showSuggestions: false,
                filteredList: [],
              },
            ];
      if (Array.isArray(prefill.members) && prefill.members.length) {
        formData.value.members = prefill.members.map((member) => ({
          name: member.name || "",
          email: member.email || "",
          appUserCode: member.appUserCode || null,
          showEmail: Boolean(member.showEmail),
        }));
      }
    };

    // モーダルが閉じられた時にフォームをリセット
    // モーダルが開かれた時にログインユーザーを初期メンバーとして設定
    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue) {
          templateImageLoadFailed.value = false;
          // モーダルが開かれた時
          console.log("[CreateShioriModal] authStore.user:", authStore.user);
          console.log("[CreateShioriModal] authStore全体:", authStore);
          const userName =
            authStore.user?.USER_NAME ||
            authStore.user?.user_name ||
            authStore.user?.USER_name;
          console.log("[CreateShioriModal] 抽出したユーザー名:", userName);
          if (userName) {
            formData.value.members = [
              {
                name: userName,
                email: "",
                appUserCode: null,
                showEmail: false,
              },
            ];
          } else {
            formData.value.members = [
              { name: "", email: "", appUserCode: null, showEmail: false },
            ];
          }
          isFriendListOpen.value = false;
          friendSearch.value = "";
          friendCandidates.value = [];
          // フォームの他のフィールドもクリア
          formData.value.title = "";
          formData.value.startDate = "";
          formData.value.endDate = "";
          formData.value.budget = null;
          formData.value.destinationType = "domestic";
          formData.value.destinations = [
            {
              value: "",
              showSuggestions: false,
              filteredList: [],
            },
          ];
          applyPrefillData();
          clearErrors();
        } else {
          // モーダルが閉じられた時
          resetForm();
        }
      },
    );

    watch(
      () => [
        props.prefillData?.templateId,
        props.prefillData?.templateImageUrl,
      ],
      () => {
        templateImageLoadFailed.value = false;
      },
    );

    return {
      formData,
      errors,
      errorMessage,
      isSubmitting,
      isDayTrip,
      isFriendListOpen,
      friendSearch,
      friendCandidates,
      imageInput,
      modalContainer,
      openImagePicker,
      imagePreview,
      displayImageUrl,
      displayImageAlt,
      isManualImagePreview,
      closeModal,
      handleSubmit,
      handleImageSelect,
      removeImage,
      handleDisplayImageError,
      addMember,
      removeMember,
      toggleMemberEmail,
      toggleFriendList,
      fetchFriendCandidates,
      addFriendMember,
      isFriendSelected,
      addDestination,
      removeDestination,
      handleDestinationInput,
      showSuggestions,
      hideSuggestions,
      selectDestination,
      validateYear,
      handleStartDateChange,
    };
  },
};
</script>

<style scoped>
/* モーダルオーバーレイ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 150; /* AppHeader(100)より上に表示 */
  padding: 1rem;
  overflow-y: auto;
}

/* モーダルコンテナ */
.modal-container {
  background: white;
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 28rem;
  max-height: calc(100vh - 8rem);
  box-shadow: var(--shadow-xl);
  animation: modal-slide-up 0.3s ease-out;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

@keyframes modal-slide-up {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* モーダルヘッダー */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-border-light);
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.modal-close-btn {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
}

.modal-close-btn:hover {
  background-color: var(--color-cool-bg);
  color: var(--color-text);
}

/* フォーム */
.modal-form {
  padding: 1.5rem;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.required {
  color: #ef4444;
}

.form-input {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 0.625rem 0.875rem;
  font-size: 0.9375rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
  background-color: white;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:hover:not(:focus) {
  border-color: var(--color-text-light);
}

.budget-input-wrapper {
  position: relative;
  min-width: 0;
}

.currency-symbol {
  position: absolute;
  top: 50%;
  left: 0.875rem;
  z-index: 1;
  color: var(--color-text-muted);
  transform: translateY(-50%);
  pointer-events: none;
}

.budget-input {
  padding-left: 2rem;
}

.input-error {
  border-color: #ef4444 !important;
  scroll-margin-block: 1rem;
}

.error-text {
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.375rem;
  scroll-margin-block: 1rem;
}

/* エラーメッセージ */
.error-message {
  margin: 1.5rem 1.5rem 0;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-left: 3px solid #ef4444;
  border-radius: var(--radius-md);
  color: #991b1b;
  font-size: 0.875rem;
  font-weight: 500;
}

/* 画像アップロードエリア */
.image-upload-area {
  position: relative;
  width: 100%;
}

.image-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.image-upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  border: 2px dashed var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-background);
  cursor: pointer;
  transition: all var(--transition-base);
}

.image-upload-label:hover {
  border-color: var(--primary-color);
  background-color: rgba(59, 130, 246, 0.05);
}

.upload-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.upload-text {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.25rem;
}

.upload-hint {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.image-preview-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-background);
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.remove-image-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 3rem;
  height: 3rem;
  min-width: 48px;
  min-height: 48px;
  padding: 0;
  box-sizing: border-box;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: all var(--transition-base);
  backdrop-filter: blur(4px);
}

.remove-image-btn:hover {
  background-color: rgba(239, 68, 68, 0.9);
  transform: scale(1.1);
}

.change-image-btn {
  position: absolute;
  left: 0.75rem;
  bottom: 0.75rem;
  min-width: 48px;
  min-height: 48px;
  padding: 0.45rem 0.875rem;
  box-sizing: border-box;
  flex: 0 0 auto;
  border: none;
  border-radius: var(--radius-full);
  background: rgba(0, 0, 0, 0.68);
  color: white;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(4px);
}

.change-image-btn:hover {
  background: rgba(0, 0, 0, 0.82);
}

.remove-image-btn:focus-visible,
.change-image-btn:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px var(--primary-color);
}

/* メンバー入力 */
.member-add-tools {
  display: flex;
  margin-bottom: 0.75rem;
}

.friend-list-toggle {
  display: inline-flex;
  gap: 0.45rem;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 0.8rem;
  border: 1px solid var(--primary-color);
  border-radius: var(--radius-md);
  background: white;
  color: var(--primary-color);
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
}

.friend-picker {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: white;
}

.friend-search-input {
  font-size: 1rem;
}

.friend-option {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.65rem;
  align-items: center;
  width: 100%;
  padding: 0.55rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
}

.friend-option:disabled {
  opacity: 0.58;
  cursor: not-allowed;
}

.friend-avatar {
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
  background: #e5e7eb;
  color: var(--color-text-muted);
}

.friend-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.friend-option strong,
.friend-option small {
  display: block;
}

.friend-option small {
  color: var(--color-text-muted);
  font-size: 0.75rem;
}

.friend-add-state {
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 700;
}

.friend-empty {
  padding: 0.75rem;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  text-align: center;
}

.members-input-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.member-input-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-background);
}

.member-input-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.member-input {
  flex: 1;
}

.member-email-input {
  background: white;
}

.member-email-toggle-btn {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid #93c5fd;
  border-radius: var(--radius-sm);
  background: #eff6ff;
  color: #2563eb;
  cursor: pointer;
  transition: all var(--transition-base);
}

.member-email-toggle-btn.active,
.member-email-toggle-btn:hover {
  background: #2563eb;
  color: white;
}

.remove-member-btn {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ef4444; /* --danger-color未定義のためリテラル値 */
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  transition: all var(--transition-base);
  flex-shrink: 0;
}

.remove-member-btn:hover {
  background-color: #dc2626;
  transform: scale(1.05);
}

.remove-member-btn:disabled {
  background-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.remove-member-btn:disabled:hover {
  background-color: #d1d5db;
  transform: none;
}

.add-member-btn {
  padding: 0.625rem 1rem;
  background-color: var(--color-background);
  color: var(--primary-color);
  border: 1px dashed var(--primary-color);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-base);
  text-align: center;
}

.add-member-btn:hover:not(:disabled) {
  background-color: rgba(59, 130, 246, 0.1);
  border-style: solid;
}

.add-member-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* モーダルフッター */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--color-border-light);
}

/* トランジション */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container {
  animation: modal-slide-up 0.3s ease-out;
}

.modal-fade-leave-active .modal-container {
  animation: modal-slide-down 0.3s ease-out;
}

@keyframes modal-slide-down {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
}

/* 渡航先選択スタイル */
.destination-type-selector {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.destination-type-btn {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.destination-type-btn:hover {
  border-color: #b4cf9e;
  background: #f7fafc;
}

.destination-type-btn.active {
  border-color: #b4cf9e;
  background: #b4cf9e;
  color: white;
}

.destinations-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.destination-input-row {
  display: flex;
  gap: 0.5rem;
  align-items: start;
}

.destination-autocomplete {
  position: relative;
  flex: 1;
}

.suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.suggestion-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.suggestion-item:hover {
  background: #f7fafc;
}

.suggestion-item:active {
  background: #edf2f7;
}

/* レスポンシブ */
/* モバイル (～640px) */
@media (max-width: 640px) {
  .modal-overlay {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 4.5rem; /* fixed headerの高さ分下げる */
  }

  .modal-container {
    max-width: 100%;
    width: 100%;
    max-height: calc(100dvh - 5rem);
    margin-bottom: 1rem;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-title {
    font-size: 1.125rem;
  }

  .close-button {
    width: 32px;
    height: 32px;
    font-size: 1.5rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .form-section + .form-section {
    margin-top: 1.25rem;
  }

  .section-title {
    font-size: 0.9375rem;
    margin-bottom: 0.75rem;
  }

  .form-group {
    margin-bottom: 0.875rem;
  }

  .form-label {
    font-size: 0.8125rem;
  }

  .form-input {
    font-size: 1rem; /* iOS zoom 防止 */
  }

  input[type="text"],
  input[type="date"],
  textarea {
    font-size: 1rem; /* iOS zoom 防止 */
  }

  .upload-icon {
    font-size: 2rem;
  }

  .upload-text {
    font-size: 0.875rem;
  }

  .upload-hint {
    font-size: 0.6875rem;
  }

  .destination-type-selector {
    gap: 0.5rem;
  }

  .destination-type-btn {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }

  .suggestion-item {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
  }

  .modal-form {
    padding: 1rem;
  }

  .modal-footer {
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 0.875rem;
  }

  .modal-footer .btn {
    font-size: 0.875rem;
    padding: 0.625rem 1rem;
  }
}

/* タブレット (641px～1024px) */
@media (min-width: 641px) and (max-width: 1024px) {
  .modal-container {
    max-width: 600px;
  }
}
</style>
